import { useMemo } from 'react';
import { useRouteMatch } from '@/routing/useRouteMatch.ts';
import { useRouterParam } from '@/routing/useRouterParam.ts';
import { unionBounds } from '@pacevault/engine/bounds.ts';
import type { GPSBounds } from '@pacevault/engine/types.ts';
import { useStudioStore } from '@/store/studio.ts';
import type { StudioRoute } from '@/types.ts';
import { COLORS_BY_STUDIO_ROUTE_COLOR } from '@/components/studio/routeColors.ts';
import { resolveLabsTab } from '@/routing/labsTab.ts';

interface StudioMapRoute {
  id: string;
  encodedPolylines: string[];
  color: [number, number, number];
  bounds: GPSBounds;
}

interface StudioMapTracks {
  active: boolean;
  focusedRouteId: string | null;
  routeId: string | null;
  routes: StudioMapRoute[];
  bounds: GPSBounds | null;
}

const toMapRoute = (route: StudioRoute): StudioMapRoute => ({
  id: route.id,
  encodedPolylines: route.encodedPolylines,
  color: COLORS_BY_STUDIO_ROUTE_COLOR[route.color].rgb,
  bounds: route.bounds,
});

export const useStudioMapTracks = (): StudioMapTracks => {
  const allRoutes = useStudioStore((s) => s.routes);
  const detailMatch = useRouteMatch('studioDetail');
  const labsMatch = useRouteMatch('labs');
  const rawTab = useRouterParam('tab') ?? null;
  const onStudioTab = labsMatch !== null && resolveLabsTab(rawTab) === 'studio';
  const detailRouteId = detailMatch?.id ?? null;

  return useMemo(() => {
    if (detailRouteId) {
      const route = allRoutes.find((r) => r.id === detailRouteId);
      if (!route) {
        return {
          active: true,
          focusedRouteId: null,
          routeId: detailRouteId,
          routes: [],
          bounds: null,
        };
      }
      return {
        active: true,
        focusedRouteId: route.id,
        routeId: detailRouteId,
        routes: [toMapRoute(route)],
        bounds: route.bounds,
      };
    }
    if (onStudioTab) {
      const routes = allRoutes.map(toMapRoute);
      return {
        active: true,
        focusedRouteId: null,
        routeId: null,
        routes,
        bounds: unionBounds(routes.map((r) => r.bounds)),
      };
    }
    return { active: false, focusedRouteId: null, routeId: null, routes: [], bounds: null };
  }, [detailRouteId, onStudioTab, allRoutes]);
};
